import React, { useState, useEffect } from "react";
import { Line } from "react-chartjs-2";
import { Modal, Button } from "react-bootstrap";
import PropTypes from "prop-types";
import axiosInstance from "../../../../../services/AxiosInstance";

// Function to calculate ideal weight using the Hamwi formula
const calculateIdealWeight = (heightInCm, gender) => {
  if (!heightInCm) return 50; // Default fallback

  // Convert height from cm to inches
  const heightInInches = heightInCm / 2.54;

  // Base height is 5 feet (60 inches)
  const inchesOver5Feet = Math.max(0, heightInInches - 60);

  // Hamwi formula
  if (gender?.toLowerCase() === "male") {
    return Math.round(48 + 2.7 * inchesOver5Feet);
  } else if (gender?.toLowerCase() === "female") {
    return Math.round(45.5 + 2.2 * inchesOver5Feet);
  }

  return 50; // Default fallback if gender is not specified
};

const WeightChart = ({ patientId, show, onClose, height, gender }) => {
  const [weightData, setWeightData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const idealWeight = calculateIdealWeight(height, gender);

  useEffect(() => {
    const fetchWeightData = async () => {
      console.log("weight",patientId);
      if (!patientId) return;

      setLoading(true);
      setError(null);
      try {
        const response = await axiosInstance.post("/admin/weightChart", {
          patient_id: patientId,
        });

        if (response.data.status === "success") {
          const sortedData = response.data.data.sort(
            (a, b) => new Date(a.created_at) - new Date(b.created_at)
          );

          setWeightData(sortedData);
        } else {
          setError("Failed to fetch weight data");
        }
      } catch (err) {
        setError(err.message || "Failed to fetch weight data");
      } finally {
        setLoading(false);
      }
    };

    if (show) {
      fetchWeightData();
    }
  }, [patientId, show]);

  const chartData = {
    labels: weightData.map((data) => {
      const date = new Date(data.created_at);
      return date.toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
      });
    }),
    datasets: [
      {
        label: "Weight",
        data: weightData.map((data) => data.weight),
        borderColor: "rgba(54,201,95,1)",
        borderWidth: 2,
        backgroundColor: "rgba(54,201,95,0.1)",
        tension: 0.4,
      },
      {
        label: "Ideal Weight",
        data: Array(weightData.length).fill(idealWeight),
        borderColor: "rgba(255,0,0,1)",
        borderWidth: 1,
        borderDash: [5, 5],
        fill: false,
      },
    ],
  };

  const options = {
    plugins: {
      legend: {
        display: true,
        position: "top",
      },
      tooltip: {
        callbacks: {
          label: function (context) {
            if (context.dataset.label === "Weight") {
              return `Weight: ${context.raw} kg`;
            } else if (context.dataset.label === "Ideal Weight") {
              return `Ideal Weight: ${idealWeight} kg`;
            }
            return context.raw;
          },
        },
      },
    },
    scales: {
      y: {
        min: 0,
        max:
          Math.max(
            100,
            ...weightData.map((data) => Number(data.weight)),
            idealWeight
          ) + 10,
        ticks: {
          beginAtZero: true,
          callback: function (value) {
            return value + " kg";
          },
        },
        title: {
          display: true,
          text: "Weight (kg)",
        },
      },
      x: {
        ticks: {
          padding: 5,
        },
        grid: {
          display: false,
        },
        title: {
          display: true,
          text: "Date",
        },
      },
    },
  };

  return (
    <Modal show={show} onHide={onClose} size="lg">
      <Modal.Header closeButton>
        <Modal.Title>Weight Chart</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {loading && <div className="text-center">Loading...</div>}
        {error && <div className="text-center text-danger">{error}</div>}
        {!loading && !error && weightData.length > 0 && (
          <>
            <Line data={chartData} options={options} />
          </>
        )}
        {!loading && !error && weightData.length === 0 && (
          <div className="text-center">No weight data available</div>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="primary" onClick={onClose}>
          Close
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

WeightChart.propTypes = {
  patientId: PropTypes.string,
  show: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  height: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  gender: PropTypes.string,
};

export default WeightChart;
